// src/lib/structuredData.js
// schema.org JSON-LD 빌더 모음 (Phase 4 — 구조화 데이터).
// 모든 함수는 순수 함수이며 평범한 JS 객체를 반환한다. 외부 의존성 없음.
// Seo 컴포넌트가 이 객체들을 <script type="application/ld+json"> 으로 직렬화해 주입한다.

export const SITE_URL = 'https://medilawyer.co.kr';

// 조직 노드의 안정적 @id. 다른 노드(@type Service/Article/Person)는 이 id 만 참조해
// 전체 조직 정보를 중복 기재하지 않는다(JSON-LD 권장 패턴).
const ORG_ID = `${SITE_URL}/#organization`;

// 절대 URL 로 정규화. path 는 항상 "/" 로 시작한다고 가정한다.
function absoluteUrl(path) {
  return `${SITE_URL}${path}`;
}

/**
 * 조직(법무법인 오킴스 의료팀)을 나타내는 LegalService 노드.
 * 사이트 전체에서 단 한 번(홈의 @graph)만 전체 정의를 싣고,
 * 나머지 페이지는 { "@id": ORG_ID } 로만 참조한다.
 * @returns {object}
 */
function organizationNode() {
  return {
    '@type': 'LegalService',
    '@id': ORG_ID,
    name: '법무법인 오킴스 의료팀',
    alternateName: 'medilawyer',
    url: SITE_URL,
    logo: absoluteUrl('/favicon-512.png'),
    image: absoluteUrl('/favicon-512.png'),
    // 의료기관·의료인을 위한 국내 법률 서비스 — 서비스 제공 지역은 대한민국.
    areaServed: {
      '@type': 'Country',
      name: '대한민국',
    },
    // 전문 분야(검색엔진이 주제 적합성을 파악하는 데 사용).
    knowsAbout: [
      '의료법',
      '의료분쟁',
      '의료사고',
      '의료과실',
      '의료형사',
      '의료행정',
      '병원 인사노무',
    ],
  };
}

/**
 * 조직 참조 노드. 다른 빌더에서 provider/publisher/worksFor 로 재사용.
 * @returns {{ '@id': string }}
 */
function orgReference() {
  return { '@id': ORG_ID };
}

/**
 * 홈(/)에 싣는 @graph. 전체 조직 정체성을 한 번 정의한다.
 * @returns {object}
 */
export function legalServiceGraph() {
  return {
    '@context': 'https://schema.org',
    '@graph': [organizationNode()],
  };
}

/**
 * 개별 법률 서비스/대응분야 페이지용 Service 노드.
 * @param {{ name: string, description: string, path: string }} params
 * @returns {object}
 */
export function serviceJsonLd({ name, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: absoluteUrl(path),
    serviceType: name,
    provider: orgReference(),
    areaServed: {
      '@type': 'Country',
      name: '대한민국',
    },
  };
}

/**
 * 사례/글 페이지용 Article 노드.
 * @param {{ title: string, description: string, path: string }} params
 * @returns {object}
 */
export function articleJsonLd({ title, description, path }) {
  const url = absoluteUrl(path);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url,
    mainEntityOfPage: url,
    publisher: orgReference(),
    author: orgReference(),
  };
}

/**
 * 변호사 프로필 페이지용 Person 노드.
 * @param {{ name: string, jobTitle: string, description: string, path: string }} params
 * @returns {object}
 */
export function personJsonLd({ name, jobTitle, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    jobTitle,
    description,
    url: absoluteUrl(path),
    worksFor: orgReference(),
  };
}

/**
 * 페이지 내비게이션 경로용 BreadcrumbList 노드.
 * @param {Array<{ name: string, path: string }>} items 홈→…→현재 순서의 경로 배열
 * @returns {object}
 */
export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}
